/**
 * Exceptions queue (uc-core src/modules/exception/exception.controller.ts):
 *   GET  /exceptions ?type&status&severity&sessionId&unitId&farmerId&runId&subjectType&subjectId&page&limit
 *   GET  /exceptions/:id
 *   POST /exceptions/:id/resolve { resolutionCode (A–Z0–9_), note?, decision? }   403 two-person rule · 409 not OPEN
 *     (a run's BALANCE_OUT_OF_BAND: resolutionCode is the cause, decision CORRECTED|ACCEPT_SHORTFALL|MOVE_TO_CONV)
 *   POST /exceptions/:id/waive   { reason }
 *   POST /containers/:id/replacement-decision { decision: VERIFY|REJECT, note? }   (F5-B1.b)
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { iso, obj, str, strOrNull } from './mappers'

export const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const
export type Severity = (typeof SEVERITIES)[number]

export interface ExceptionFilters extends PageParams {
  type?: string
  status?: 'OPEN' | 'RESOLVED' | 'WAIVED' | 'ALL'
  severity?: Severity | 'ALL'
  sessionId?: string
  unitId?: string
  farmerId?: string
  /** A processing run's exceptions (machine processing log). */
  runId?: string
  subjectType?: string
  subjectId?: string
}

export interface ExceptionRow {
  id: string
  type: string
  severity: string
  status: string
  subjectType: string
  subjectId: string
  sessionId: string | null
  farmerId: string | null
  runId: string | null
  eventId: string | null
  detail: Record<string, unknown>
  raisedAt: string | null
  raisedById: string | null
  resolutionCode: string | null
  resolvedById: string | null
  resolvedAt: string | null
}

export function mapException(r: Record<string, unknown>): ExceptionRow {
  return {
    id: str(r.id),
    type: str(r.type),
    severity: str(r.severity, 'LOW'),
    status: str(r.status, 'OPEN'),
    subjectType: str(r.subjectType ?? r.subject_type),
    subjectId: str(r.subjectId ?? r.subject_id),
    sessionId: strOrNull(r.sessionId ?? r.session_id),
    farmerId: strOrNull(r.farmerId ?? r.farmer_id),
    runId: strOrNull(r.runId ?? r.run_id),
    eventId: strOrNull(r.eventId ?? r.event_id),
    detail: obj(r.detail),
    raisedAt: iso(r.raisedAt ?? r.raised_at),
    raisedById: strOrNull(r.raisedById ?? r.raised_by_id),
    resolutionCode: strOrNull(r.resolutionCode ?? r.resolution_code),
    resolvedById: strOrNull(r.resolvedById ?? r.resolved_by_id),
    resolvedAt: iso(r.resolvedAt ?? r.resolved_at),
  }
}

export const fetchExceptions = (f: ExceptionFilters) => getPage('/exceptions', f, mapException)

export function useExceptions(f: ExceptionFilters, enabled = true) {
  return usePageQuery([...ck.exceptions, 'list', f], () => fetchExceptions(f), { enabled })
}

export function useException(id: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.exceptions, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/exceptions/${id}`, mapException),
  })
}

const TOUCHED = [ck.exceptions, ck.lots, ck.sessions, ck.payouts, ck.dashboard, ck.stock, ck.shipments]

export function useResolveException() {
  return useConsoleMutation<{ id: string; resolutionCode: string; note?: string; decision?: RunDecision }>({
    mutationFn: ({ id, resolutionCode, note, decision }) =>
      api.post(`/exceptions/${id}/resolve`, { resolutionCode, note: note || undefined, decision }),
    invalidate: TOUCHED,
    success: 'Exception resolved',
    errorTitle: 'Not resolved',
  })
}

export function useWaiveException() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/exceptions/${id}/waive`, { reason }),
    invalidate: TOUCHED,
    success: 'Exception waived',
    errorTitle: 'Not waived',
  })
}

export function useReplacementDecision() {
  return useConsoleMutation<
    { containerId: string; decision: 'VERIFY' | 'REJECT'; note?: string },
    { unitCode?: string }
  >({
    mutationFn: ({ containerId, decision, note }) =>
      api.post(`/containers/${containerId}/replacement-decision`, { decision, note: note || undefined }),
    invalidate: TOUCHED,
    success: (_r, v) => (v.decision === 'VERIFY' ? 'Replacement verified' : 'Replacement rejected — bag is an orphan'),
    errorTitle: 'Decision not saved',
  })
}

/** How a run's BALANCE_OUT_OF_BAND is decided (uc-core RunDecision, run-decision.service). */
export type RunDecision = 'CORRECTED' | 'ACCEPT_SHORTFALL' | 'MOVE_TO_CONV'

/**
 * Exception types that cannot be waived (server refuses) — the UI hides Waive for them. A run's
 * BALANCE_OUT_OF_BAND is decided with a cause instead (a plain waive has no cause and is refused).
 */
export const NEVER_WAIVED = new Set(['DISPUTE', 'BALANCE_OUT_OF_BAND'])

/** Common resolution codes offered in the resolve dialog, by type (free text is allowed too). */
export const RESOLUTION_CODES: Record<string, string[]> = {
  DISPUTE: ['SUPERVISOR_UPHELD'],
  TAG_CONFLICT: ['RETAGGED', 'ROLL_VOIDED', 'CHECKED_OK'],
  SESSION_UNRECONCILED: ['COUNTED_AGAIN', 'ROLL_VOIDED', 'EXPLAINED'],
  BAG_COUNT_MISMATCH: ['COUNTED_AGAIN', 'EXPLAINED'],
  PAPER_MISMATCH: ['CORRECTED', 'SLIP_STANDS'],
  REWEIGH_VARIANCE: ['REWEIGHED_OK', 'DRYING_EXPLAINED', 'CORRECTED'],
  PAYMENT_FAILED: ['PAID_AGAIN', 'BANK_FIXED'],
  CUSTODY_RULE: ['CHECKED_OK', 'MOVED'],
  COUNT_MISMATCH: ['RECOUNTED', 'FOUND', 'EXPLAINED'],
  PHOTO_MISSING: ['PHOTO_ARRIVED', 'NOT_NEEDED'],
  LATE_ENTRY: ['CHECKED_OK'],
}
export const DEFAULT_RESOLUTION_CODES = ['CHECKED_OK', 'CORRECTED', 'EXPLAINED']

/** A run's problem (any type) closes only with its cause (uc-core RUN_CAUSES, run-decision.service). */
export const RUN_CAUSE_CODES = [
  'UNWEIGHED_SACK',
  'SCALE_ERROR',
  'TYPING_ERROR',
  'BAG_IN_WITHOUT_SCAN',
  'HIGH_MOISTURE',
  'MACHINE_FAULT',
  'BAND_WRONG',
  'UNEXPLAINED_LOSS',
  'MIXING',
]

/** Link target for an exception's subject inside the console. */
export function subjectLink(e: Pick<ExceptionRow, 'subjectType' | 'subjectId' | 'detail' | 'sessionId'>): string | null {
  const d = e.detail
  switch (e.subjectType) {
    case 'UNIT':
      return `/c/lots/${e.subjectId}`
    case 'SESSION':
      return `/c/sessions/${e.subjectId}`
    case 'SHIPMENT':
      return `/c/shipments/${e.subjectId}`
    case 'LOCATION':
      return `/c/locations?focus=${e.subjectId}`
    case 'PAYOUT':
      return `/c/payouts?open=${e.subjectId}`
    case 'CONTAINER':
    case 'SAMPLE':
      if (typeof d.unit_id === 'string') return `/c/lots/${d.unit_id}`
      return e.sessionId ? `/c/sessions/${e.sessionId}` : null
    default:
      return e.sessionId ? `/c/sessions/${e.sessionId}` : null
  }
}
